
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { api } from '../../services/api';
import OTPDialog from '../../components/OtpDialog';
import { tokenStorage } from '../../utils/tokenStorage';

const Signup = () => {
  const [userName, setUserName] = useState('');
  const [userContactNumber, setUserContactNumber] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userAddress, setUserAddress] = useState('');
  const [userOfficeAddress, setUserOfficeAddress] = useState('');
  const [userOccupation, setUserOccupation] = useState('');
  const [userEmergencyContactNumber, setUserEmergencyContactNumber] = useState('');
  const [userGender, setUserGender] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    try {
      const payload = {
        userName,
        userContactNumber,
        userEmail,
        userAddress,
        userOfficeAddress,
        userOccupation,
        userEmergencyContactNumber,
        userGender,
        userPassword,
        isActive: false,
        lastLoginAt: Date.now(),
      };

      const res = await api.post('/users/signup', payload);

      if (res.data?.status === 200) {
        setMessage('Signup successful! Please verify your email with the OTP sent.');
        setShowOTP(true);
      } else {
        setMessage(res.data?.message || 'Signup failed');
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setMessage(
          error.response.data?.message ||
            'An account with this email already exists. Please log in.'
        );
      } else {
        setMessage(error.response?.data?.message || 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
      <div className="bg-white shadow-md rounded-lg p-8 max-w-md w-full">
        <h2 className="text-2xl font-semibold text-center mb-6">Sign Up</h2>
        <form onSubmit={handleSignup} className="space-y-4">
          <TextField
            label="Full Name"
            variant="outlined"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Contact Number"
            variant="outlined"
            type="tel"
            value={userContactNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                setUserContactNumber(value);
              }
            }}
            fullWidth
            required
            slotProps={{
              htmlInput: {
                maxLength: 10,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              },
            }}
          />

          <TextField
            label="Email"
            variant="outlined"
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Home Address"
            variant="outlined"
            value={userAddress}
            onChange={(e) => setUserAddress(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Office Address"
            variant="outlined"
            value={userOfficeAddress}
            onChange={(e) => setUserOfficeAddress(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Occupation"
            variant="outlined"
            value={userOccupation}
            onChange={(e) => setUserOccupation(e.target.value)}
            fullWidth
            required
            placeholder="e.g., Engineer, Doctor, Student"
          />

          <TextField
            label="Emergency Contact Number"
            variant="outlined"
            type="tel"
            value={userEmergencyContactNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                setUserEmergencyContactNumber(value);
              }
            }}
            fullWidth
            required
            slotProps={{
              htmlInput: {
                maxLength: 10,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              },
            }}
          />

          <FormControl fullWidth required>
            <InputLabel>Gender</InputLabel>
            <Select
              value={userGender}
              onChange={(e) => setUserGender(e.target.value)}
              label="Gender"
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Password"
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            value={userPassword}
            onChange={(e) => setUserPassword(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                endAdornment: (
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none px-2"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                ),
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? 'Signing up...' : 'Sign Up'}
          </Button>
        </form>

        <p className="mt-4 text-sm text-center">
          Already have an account?{' '}
          <Link to="/" className="text-blue-600 hover:underline">
            Login
          </Link>
        </p>

        {message && (
          <p
            className={`mt-4 text-center ${
              message.toLowerCase().includes('success')
                ? 'text-green-600'
                : 'text-red-500'
            }`}
          >
            {message}
          </p>
        )}
      </div>

      <OTPDialog
  open={showOTP}
  onClose={() => setShowOTP(false)}
  email={userEmail}
  onVerified={(verifiedPayload) => {
    setShowOTP(false);

    // verify_email now returns LoginPayload { accessToken, refreshToken, user }
    const user = verifiedPayload?.user;
    const accessToken = verifiedPayload?.accessToken;
    const refreshToken = verifiedPayload?.refreshToken;

    if (accessToken && refreshToken) {
      tokenStorage.saveTokens(accessToken, refreshToken);
    }
    if (user) {
      tokenStorage.saveUser(user);
    }

    if (user?.userRole === 'Admin') {
      navigate('/admin-dashboard', { replace: true });
    } else {
      navigate('/user-dashboard', { replace: true });
    }
  }}
/>
    </div>
  );
};

function EyeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );
}

export default Signup;